/**
 * Extensiones TipTap compartidas — fuente única para el editor de
 * artefactos (B.6.b).
 *
 * Stack mínimo Sprint 1: StarterKit + tipografía + highlight + link +
 * text-align + underline + sub/superscript + placeholder +
 * character count + table.
 *
 * NO incluye nodos custom de HIVA (citas a Sources, refs entre
 * artefactos, comentarios, suggestions). Esos llegan en sprints
 * siguientes (3+) cuando el modelo de documentos oficiales esté maduro y aparezca
 * el flow de review (B.6.c.2) y el RAG (Sprint C de Sources).
 *
 * También NO incluye imágenes — entran cuando llegue Azure Blob real
 * (Fase F) con signed URLs.
 *
 * StarterKit en TipTap 3 incluye por default: Document, Paragraph, Text,
 * Bold, Italic, Strike, Code, CodeBlock, Heading, BulletList,
 * OrderedList, ListItem, Blockquote, HardBreak, HorizontalRule, History,
 * Dropcursor, Gapcursor + Link config básica. Limitamos heading a H2/H3
 * (los artefactos viven dentro de un viewer que ya tiene su propio H1
 * para el título de la sección).
 */

import CharacterCount from '@tiptap/extension-character-count'
import Highlight from '@tiptap/extension-highlight'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import Subscript from '@tiptap/extension-subscript'
import Superscript from '@tiptap/extension-superscript'
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table'
import TextAlign from '@tiptap/extension-text-align'
import Typography from '@tiptap/extension-typography'
import StarterKit from '@tiptap/starter-kit'
import type { Extensions } from '@tiptap/react'

import { CitationMark } from './CitationMark'

export interface BuildExtensionsOptions {
  /** Texto del placeholder cuando el editor está vacío. */
  placeholder?: string
}

/** Construye la lista de extensiones. Acepta opciones (placeholder) que
 * varían por sección — el resto del setup es estático. */
export function buildEditorExtensions(
  options: BuildExtensionsOptions = {},
): Extensions {
  return [
    // StarterKit en TipTap 3 ya incluye: Blockquote, Bold, Code, CodeBlock,
    // HardBreak, Heading, HorizontalRule, Italic, Strike, Underline,
    // Link, BulletList/OrderedList/ListItem/ListKeymap, Paragraph,
    // Dropcursor, UndoRedo, TrailingNode. Por eso NO añadimos Underline
    // ni Link separados — duplicarlos rompe la inicialización (el editor
    // queda en null y los rich_text no renderizan).
    //
    // Desactivamos `link` del StarterKit y lo re-configuramos aparte
    // porque queremos opciones específicas (autolink + openOnClick=false
    // + nofollow). Si dejaramos el de StarterKit, se aplicaría la config
    // default.
    StarterKit.configure({
      heading: { levels: [2, 3] },
      link: false,
    }),
    Subscript,
    Superscript,
    Highlight.configure({ multicolor: false }),
    Typography,
    Link.configure({
      openOnClick: false,
      autolink: true,
      linkOnPaste: true,
      HTMLAttributes: {
        rel: 'noopener noreferrer nofollow',
        target: '_blank',
      },
    }),
    TextAlign.configure({
      types: ['heading', 'paragraph'],
      alignments: ['left', 'center', 'right'],
      defaultAlignment: 'left',
    }),
    Table.configure({ resizable: false }),
    TableRow,
    TableHeader,
    TableCell,
    Placeholder.configure({
      placeholder: options.placeholder ?? '',
      emptyEditorClass: 'is-editor-empty',
    }),
    CharacterCount.configure(),
    // ADR-0001 Tier 1 — citas IA inline `[N]`. Mark sin UI propia: el
    // viewer/editor decide cómo renderizar el `<cite>` (popover, tooltip).
    CitationMark,
  ]
}
